
// src/components/checagens/ChecklistCard.tsx
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChecklist, useUpdateChecklistItem } from '../../hooks/useChecagens';
import { ChecklistItem, CanalChecklist, StatusCanal } from '../../models/checagens';
import { CANAL_STATUS_COLORS } from '../../utils/colors';
import Loading from '../Loading';
import ErrorToast from '../ErrorToast';
import SuccessToast from '../SuccessMessage'; // Assuming SuccessMessage is the success toast
import './ChecklistCard.css';

interface ChecklistCardProps {
  checagemImovelId: number;
}

const ChecklistCard: React.FC<ChecklistCardProps> = ({ checagemImovelId }) => {
  const queryClient = useQueryClient();
  const { data: checklist, isLoading, error } = useChecklist(checagemImovelId);
  const updateMutation = useUpdateChecklistItem();

  const [localChecklist, setLocalChecklist] = useState<ChecklistItem[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (checklist) {
      setLocalChecklist(checklist);
    }
  }, [checklist]);

  const handleInputChange = (canal: CanalChecklist, field: string, value: string | number) => {
    setLocalChecklist(prev =>
      prev.map(item => item.canal === canal ? { ...item, [field]: value } : item)
    );
  };

  const handleSave = async (canal: CanalChecklist) => {
    const itemToSave = localChecklist.find(item => item.canal === canal);
    if (!itemToSave) return;

    const previousChecklist = queryClient.getQueryData<ChecklistItem[]>(['checagemImovel', checagemImovelId, 'checklist']);

    // Optimistic update
    queryClient.setQueryData<ChecklistItem[]>(['checagemImovel', checagemImovelId, 'checklist'], oldData =>
      oldData?.map(item => item.canal === canal ? itemToSave : item) ?? []
    );

    try {
      await updateMutation.mutateAsync(
        {
          ciId: checagemImovelId, canal, data: {
            status_canal: itemToSave.status_canal,
            anotacoes: itemToSave.anotacoes ?? undefined // garante string ou undefined
          }
        }, {
        onSuccess: () => {
          setSuccessMessage(`Checklist de ${canal} salvo com sucesso!`);
          setTimeout(() => setSuccessMessage(null), 3000);
          queryClient.invalidateQueries({ queryKey: ['checagens'] }); // To update progress bars
        },
        onError: () => {
          // Rollback
          if (previousChecklist) {
            queryClient.setQueryData(['checagemImovel', checagemImovelId, 'checklist'], previousChecklist);
          }
        }
      }
      );
    } catch (e) {
      // Error is handled by onError in mutateAsync
    }
  };

  if (isLoading) return <div className="card"><Loading /></div>;
  if (error) return <div className="card"><ErrorToast message={error.message} onClose={() => { }} /></div>;

  return (
    <div className="card checklist-card">
      <h3>Checklist de Contato</h3>
      {successMessage && <SuccessToast message={successMessage} onClose={() => setSuccessMessage(null)} />}
      {updateMutation.isError && <ErrorToast message={updateMutation.error.message} onClose={() => { }} />}

      {localChecklist.map(item => (
        <div key={item.canal} className="checklist-item">
          <label style={{ borderLeftColor: CANAL_STATUS_COLORS[item.status_canal] }}>{item.canal}</label>
          <select
            value={item.status_canal}
            onChange={e => handleInputChange(item.canal, 'status_canal', e.target.value as StatusCanal)}
          >
            <option value="nao_iniciado">Não Iniciado</option>
            <option value="tentado_sem_sucesso">Tentado sem Sucesso</option>
            <option value="sucesso_sem_retorno">Sucesso sem Retorno</option>
            <option value="sucesso_com_retorno">Sucesso com Retorno</option>
          </select>
          <input
            type="number"
            placeholder="Tentativas"
            value={item.tentativas}
            readOnly
            className="disabled-input"
          />
          <textarea
            placeholder="Anotações..."
            value={item.anotacoes || ''}
            onChange={e => handleInputChange(item.canal, 'anotacoes', e.target.value)}
          />
          <button onClick={() => handleSave(item.canal)} disabled={updateMutation.isPending}>
            Salvar
          </button>
        </div>
      ))}
    </div>
  );
};

export default ChecklistCard;
